import React, { useEffect, useMemo, useState } from 'react';
import {
  FileSpreadsheet,
  RefreshCw,
  Loader2,
  Search,
  ShieldCheck,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const CHOICE_COLOR = {
  accepted: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30',
  rejected: 'bg-destructive/15 text-destructive border border-destructive/30',
  withdrawn: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30',
};

function formatTs(ts) {
  if (!ts) return '';
  try {
    return format(new Date(ts), "MMM d, yyyy 'at' HH:mm:ss 'UTC'");
  } catch (_e) {
    return ts;
  }
}

export function ConsentLogPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState(null);
  const [consentVersion, setConsentVersion] = useState('');

  // Filters
  const [choice, setChoice] = useState('');           // '' | 'accepted' | 'rejected' | 'withdrawn'
  const [dateFrom, setDateFrom] = useState('');       // 'YYYY-MM-DD'
  const [dateTo, setDateTo] = useState('');           // 'YYYY-MM-DD'
  const [q, setQ] = useState('');

  const buildParams = (override = {}) => {
    const p = {
      page,
      page_size: pageSize,
      ...(choice ? { choice } : {}),
      ...(dateFrom ? { date_from: `${dateFrom}T00:00:00Z` } : {}),
      ...(dateTo ? { date_to: `${dateTo}T23:59:59Z` } : {}),
      ...(q ? { q } : {}),
      ...override,
    };
    return p;
  };

  const fetchPage = async (override = {}) => {
    setLoading(true);
    try {
      const r = await adminApi.listConsentLog(buildParams(override));
      setItems(r?.items || []);
      setTotal(r?.total || 0);
      setRetention(r?.retention_days ?? null);
      setConsentVersion(r?.consent_version || '');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load consent log'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = () => {
    setPage(1);
    fetchPage({ page: 1 });
  };

  const clearFilters = () => {
    setChoice('');
    setDateFrom('');
    setDateTo('');
    setQ('');
    setPage(1);
    fetchPage({ page: 1, choice: undefined, date_from: undefined, date_to: undefined, q: undefined });
  };

  const onDownloadCSV = () => {
    // Use the helper that builds an authenticated download via a one-off
    // fetch with the Authorization header; the API URL itself doesn't
    // carry the token. We trigger the same export endpoint and stream
    // the response into a blob so the browser triggers a download.
    const token = localStorage.getItem('le_token');
    const params = new URLSearchParams(Object.entries({
      ...(choice ? { choice } : {}),
      ...(dateFrom ? { date_from: `${dateFrom}T00:00:00Z` } : {}),
      ...(dateTo ? { date_to: `${dateTo}T23:59:59Z` } : {}),
      ...(q ? { q } : {}),
    }).filter(([, v]) => v !== undefined && v !== '')).toString();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/consent-log/export.csv${params ? `?${params}` : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Export failed (${res.status})`);
        }
        return res.blob();
      })
      .then((blob) => {
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = `consent-log-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(href);
        toast.success('CSV downloaded');
      })
      .catch((e) => toast.error(getErrorMessage(e, 'Could not export CSV')));
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const retentionYears = retention ? Math.round(retention / 365) : null;

  return (
    <Card className="border-border" data-testid="consent-log-panel">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <ShieldCheck size={18} className="text-primary" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold">Consent log</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every accept / reject / withdraw event is recorded with a hashed IP, browser
              agent, and timestamp for GDPR / UK PECR audits.{' '}
              {retentionYears && (
                <span>
                  Records auto-expire after <span className="font-mono-edge">{retentionYears} years</span>.
                </span>
              )}{' '}
              {consentVersion && (
                <span>
                  Current consent text version: <span className="font-mono-edge">{consentVersion}</span>.
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPage()}
            disabled={loading}
            data-testid="consent-log-refresh-btn"
          >
            {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={onDownloadCSV}
            disabled={loading || total === 0}
            data-testid="consent-log-csv-btn"
            className="rounded-xl"
          >
            <Download size={12} className="mr-1.5" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Choice</Label>
            <Select value={choice || 'all'} onValueChange={(v) => setChoice(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="consent-log-choice-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All choices</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="withdrawn">Withdrawn</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="cl-from">From</Label>
            <Input
              id="cl-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="consent-log-from-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="cl-to">To</Label>
            <Input
              id="cl-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="consent-log-to-filter"
            />
          </div>
          <div className="space-y-1.5 md:col-span-1">
            <Label className="text-xs" htmlFor="cl-q">Search</Label>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="cl-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="email / client_id"
                className="pl-7"
                data-testid="consent-log-q-filter"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} disabled={loading} className="rounded-xl flex-1" data-testid="consent-log-apply-btn">
              Apply
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading} className="rounded-xl" data-testid="consent-log-clear-btn">
              Clear
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading && items.length === 0 ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="consent-log-empty">
            <FileSpreadsheet size={24} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No consent events match the current filters.
            </p>
          </div>
        ) : (
          <Table data-testid="consent-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Choice</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Client ID</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>IP hash</TableHead>
                <TableHead>Version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`consent-row-${row.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{formatTs(row.created_at)}</TableCell>
                  <TableCell>
                    <Badge className={`font-mono-edge text-[10px] ${CHOICE_COLOR[row.choice] || ''}`}>
                      {row.choice}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.user_email ? (
                      <span className="font-medium">{row.user_email}</span>
                    ) : (
                      <span className="text-muted-foreground">— anonymous —</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono-edge text-muted-foreground max-w-[160px] truncate" title={row.client_id}>
                    {row.client_id}
                  </TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate" title={row.page_url}>
                    {row.page_url || '—'}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono-edge text-muted-foreground max-w-[140px] truncate" title={row.ip_hash}>
                    {row.ip_hash || '—'}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono-edge">{row.consent_version}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground" data-testid="consent-log-pagination">
            <span>
              Showing <span className="font-mono-edge">{(page - 1) * pageSize + 1}</span>–
              <span className="font-mono-edge">{Math.min(total, page * pageSize)}</span> of{' '}
              <span className="font-mono-edge">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                data-testid="consent-log-prev"
              >
                <ChevronLeft size={12} className="mr-1" /> Prev
              </Button>
              <span className="font-mono-edge text-[11px]">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                data-testid="consent-log-next"
              >
                Next <ChevronRight size={12} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ConsentLogPanel;
